/*
 * AAIKO announcement bar rotator.
 *
 *   aaiko-announcement-rotator   cross-fades the bar's messages on a timer
 *
 * The bar renders every message at once, stacked in the same box, with
 * `data-current` on the one that is visible. All this does is move that
 * attribute; the cross-fade itself is CSS (sections/aaiko-announcement.liquid),
 * so the bar already shows its first message with the script still loading,
 * or not loading at all.
 *
 * Three states per message, all attribute-driven:
 *   data-current   visible
 *   data-leaving   on its way out, one transition long
 *   neither        parked below, invisible
 *
 * Deliberately NOT an aria-live region: the messages are static promo copy a
 * screen reader has already read in source order, and announcing a
 * timer-driven swap would interrupt whatever the visitor is actually doing.
 *
 * WCAG 2.2.2 - auto-updating content has to be stoppable. It pauses while the
 * pointer is over the bar or focus is inside it, while the tab is hidden, and
 * it never starts under prefers-reduced-motion, which leaves the first message
 * in place, static.
 *
 * Nothing here is drawn: the Figma frames carry one message, one state.
 */
(function () {
  'use strict';

  if (customElements.get('aaiko-announcement-rotator')) return;

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  customElements.define(
    'aaiko-announcement-rotator',
    class AaikoAnnouncementRotator extends HTMLElement {
      connectedCallback() {
        this.messages = Array.prototype.slice.call(this.querySelectorAll('.aaiko-announcement__text'));
        /* One message is a static bar, which is the whole point of the
           fallback: nothing to rotate, no timer, no listeners. */
        if (this.messages.length < 2) return;

        /* Taking over from the CSS fallback: until `data-ready` is on the host,
           the stylesheet keeps the first message visible through
           :first-of-type, which is what a visitor sees with the script still
           loading, blocked, or absent. Both attributes are set in the same
           task, so the handover paints as one frame and nothing flashes. */
        this.index = 0;
        this.messages[0].setAttribute('data-current', '');
        this.setAttribute('data-ready', '');

        /* Seconds in the editor, milliseconds here. Floored so a merchant
           cannot set a cadence faster than the fade it is driving. */
        var seconds = parseFloat(this.getAttribute('data-interval'));
        if (!(seconds > 0)) seconds = 3;
        this.interval = Math.max(1500, seconds * 1000);

        this.holds = 0;
        this.timer = null;

        /* Pointer events carry a pointerType; focus events do not, and those
           must always hold. `event && event.pointerType` is the whole test:
           a touch contact reports 'touch' and is ignored, focusin reports
           nothing and holds. */
        this.onEnter = function (event) {
          if (event && event.pointerType && event.pointerType === 'touch') return;
          this.hold(true);
        }.bind(this);
        this.onLeave = function (event) {
          if (event && event.pointerType && event.pointerType === 'touch') return;
          this.hold(false);
        }.bind(this);
        this.onVisibility = this.sync.bind(this);
        this.onMotion = this.sync.bind(this);

        /* CLIENT BUG (feedback 28/08/2026): "mobiel USP top banner navigeert
           niet automatisch naar de volgende kopjes". A touch that starts on the
           bar - which on mobile is the top 28px of the page, so any downward
           swipe from the very top - fires pointerenter and then, once the
           browser claims the gesture for scrolling, pointerCANCEL. There is no
           pointerleave, so the hold that pointerenter took was never released
           and the bar stopped rotating for the rest of the session.

           Two guards. The hold is now only taken for a hovering pointer
           (mouse, pen), which is the input the pause was written for - WCAG
           2.2.2 wants the visitor able to stop the rotation to read a message,
           and on touch that is what stopping the scroll does. And
           pointercancel releases like pointerleave, so a stray hold from any
           other path cannot latch either. */
        this.addEventListener('pointerenter', this.onEnter);
        this.addEventListener('pointerleave', this.onLeave);
        this.addEventListener('pointercancel', this.onLeave);
        this.addEventListener('focusin', this.onEnter);
        this.addEventListener('focusout', this.onLeave);
        document.addEventListener('visibilitychange', this.onVisibility);
        /* Still listened to while reduced motion is on, so turning the
           preference off mid-session starts the rotation without a reload. */
        if (REDUCED.addEventListener) REDUCED.addEventListener('change', this.onMotion);
        else if (REDUCED.addListener) REDUCED.addListener(this.onMotion);

        this.sync();
      }

      disconnectedCallback() {
        this.stop();
        if (!this.onEnter) return;
        this.removeEventListener('pointerenter', this.onEnter);
        this.removeEventListener('pointerleave', this.onLeave);
        this.removeEventListener('pointercancel', this.onLeave);
        this.removeEventListener('focusin', this.onEnter);
        this.removeEventListener('focusout', this.onLeave);
        document.removeEventListener('visibilitychange', this.onVisibility);
        if (REDUCED.removeEventListener) REDUCED.removeEventListener('change', this.onMotion);
        else if (REDUCED.removeListener) REDUCED.removeListener(this.onMotion);
      }

      /* A counter rather than a boolean: the pointer can leave the bar while
         focus is still inside it, and a boolean would resume on the first of
         the two to release. */
      hold(on) {
        this.holds = Math.max(0, this.holds + (on ? 1 : -1));
        this.sync();
      }

      /* CLIENT REPORT (feedback 31/08/2026): "deze doet nog steeds niets."
         Verified in the browser the same day: with prefers-reduced-motion on,
         the bar never advanced; with it off, it did. Reduce Motion is on by
         default for a lot of people on iOS, so for them the bar had one message
         and no rotation at all - which is exactly what AAIKO was looking at.

         The preference is about vestibular motion, not about content changing.
         What it should switch off here is the 4px travel, and the stylesheet
         already does that: under the preference the messages cross-fade in
         place with no movement. Rotating on is therefore the right reading, and
         stopping was over-applying it.

         WCAG 2.2.2 is unaffected. It asks for auto-updating content to be
         pausable, and the bar still pauses on hover, on focus and while the tab
         is hidden - none of which is this preference. */
      sync() {
        if (this.holds > 0 || document.hidden) this.stop();
        else this.start();
      }

      start() {
        if (this.timer) return;
        this.timer = setInterval(this.advance.bind(this), this.interval);
      }

      stop() {
        if (!this.timer) return;
        clearInterval(this.timer);
        this.timer = null;
      }

      advance() {
        var out = this.messages[this.index];
        this.index = (this.index + 1) % this.messages.length;
        var next = this.messages[this.index];

        /* The incoming message gives up `data-leaving` first: on a two-message
           bar it is the one that just left, and it has to be parked below
           again before it is asked to rise. Reading offsetWidth between the
           two writes forces that reset to land as a layout rather than as a
           transition the browser would fold into the entrance. */
        if (next.hasAttribute('data-leaving')) {
          next.removeAttribute('data-leaving');
          void next.offsetWidth;
        }

        out.removeAttribute('data-current');
        out.setAttribute('data-leaving', '');
        next.setAttribute('data-current', '');
      }
    }
  );
})();
